import { useState } from 'react'
import { X, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { EvalOutputField } from '@super/eval-framework'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AssertionRow {
  id: string
  field: string // EvalOutputField.key, or '' if not yet chosen
  operator: string // '$exact' | '$contains' | '$in' | '$gt' | '$gte' | '$lt' | '$lte', or ''
  value: string // raw string; multi-line for $in
}

// ─── Operator definitions ────────────────────────────────────────────────────

const OPERATOR_LABELS: Record<string, string> = {
  $exact: 'Equals',
  $contains: 'Contains',
  $in: 'Is one of',
  $gt: 'Greater than',
  $gte: 'At least',
  $lt: 'Less than',
  $lte: 'At most',
}

// text[] fields have different array-aware semantics
const TEXT_ARRAY_OPERATOR_LABELS: Record<string, string> = {
  $contains: 'Includes',
  $in: 'Includes any of',
  $empty: 'Is empty',
}

function getOperators(type: EvalOutputField['type']): string[] {
  if (type === 'text[]') return ['$contains', '$in', '$empty']
  if (type === 'number') return ['$exact', '$in', '$gt', '$gte', '$lt', '$lte']
  return ['$exact', '$contains', '$in']
}

function getOperatorLabel(type: EvalOutputField['type'], op: string): string {
  if (type === 'text[]') return TEXT_ARRAY_OPERATOR_LABELS[op] ?? op
  return OPERATOR_LABELS[op] ?? op
}

// ─── Shared assertion helpers ────────────────────────────────────────────────

/** Converts assertion rows into the expectedOutput MatcherSpec map. */
export function serializeAssertions(
  assertions: AssertionRow[],
  outputFields: EvalOutputField[],
): Record<string, unknown> {
  const expectedOutput: Record<string, unknown> = {}
  for (const { field, operator: op, value: raw } of assertions) {
    if (!field || !op) continue
    if (op === '$empty') {
      expectedOutput[field] = { $empty: true }
      continue
    }
    if (!raw.trim()) continue
    const fieldType = outputFields.find(f => f.key === field)?.type
    if (op === '$in') {
      const items = raw
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
      expectedOutput[field] = {
        $in: fieldType === 'number' ? items.map(Number) : items,
      }
    } else if (op === '$contains') {
      expectedOutput[field] = { $contains: raw }
    } else if (['$gt', '$gte', '$lt', '$lte'].includes(op)) {
      expectedOutput[field] = { [op]: Number(raw) }
    } else {
      expectedOutput[field] = {
        $exact: fieldType === 'number' ? Number(raw) : raw,
      }
    }
  }
  return expectedOutput
}

/** True when at least one assertion row is fully specified. */
export function hasCompleteAssertion(assertions: AssertionRow[]): boolean {
  return assertions.some(a => {
    if (!a.field || !a.operator) return false
    if (a.operator === '$empty') return true
    if (a.operator === '$in')
      return (a.value ?? '').split(',').some(s => s.trim().length > 0)
    return (a.value ?? '').trim().length > 0
  })
}

// ─── AssertionBuilder ────────────────────────────────────────────────────────

interface AssertionBuilderProps {
  fields: EvalOutputField[]
  value: AssertionRow[]
  onChange: (rows: AssertionRow[]) => void
}

export function AssertionBuilder({
  fields,
  value,
  onChange,
}: AssertionBuilderProps) {
  const handleAdd = () => {
    onChange([
      ...value,
      { id: crypto.randomUUID(), field: '', operator: '', value: '' },
    ])
  }

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const handleUpdate = (index: number, updates: Partial<AssertionRow>) => {
    onChange(
      value.map((row, i) => (i === index ? { ...row, ...updates } : row)),
    )
  }

  return (
    <div className="space-y-3">
      {value.map((row, index) => (
        <AssertionRowItem
          key={row.id}
          row={row}
          fields={fields}
          onUpdate={updates => handleUpdate(index, updates)}
          onRemove={() => handleRemove(index)}
        />
      ))}
      <Button onClick={handleAdd} variant="outline" size="sm" type="button">
        <Plus className="h-4 w-4 mr-2" />
        Add Assertion
      </Button>
    </div>
  )
}

// ─── AssertionRowItem ────────────────────────────────────────────────────────

interface AssertionRowItemProps {
  row: AssertionRow
  fields: EvalOutputField[]
  onUpdate: (updates: Partial<AssertionRow>) => void
  onRemove: () => void
}

function AssertionRowItem({
  row,
  fields,
  onUpdate,
  onRemove,
}: AssertionRowItemProps) {
  const [fieldOpen, setFieldOpen] = useState(false)
  const [operatorOpen, setOperatorOpen] = useState(false)
  const [valueOpen, setValueOpen] = useState(false)

  const fieldConfig = fields.find(f => f.key === row.field)
  const fieldType = fieldConfig?.type ?? 'text'
  const availableOperators = fieldConfig ? getOperators(fieldConfig.type) : []

  const handleFieldSelect = (key: string) => {
    onUpdate({ field: key, operator: '', value: '' })
    setFieldOpen(false)
    setOperatorOpen(true)
  }

  const handleOperatorSelect = (op: string) => {
    onUpdate({ operator: op, value: '' })
    setOperatorOpen(false)
    setValueOpen(true)
  }

  const isMultiValue = row.operator === '$in'
  const isNumeric =
    ['$gt', '$gte', '$lt', '$lte'].includes(row.operator) ||
    (fieldType === 'number' && row.operator === '$exact')

  return (
    <div className="flex items-start gap-0 flex-wrap">
      {/* Field selector */}
      <Popover open={fieldOpen} onOpenChange={setFieldOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'group relative h-9 px-4 text-sm transition-colors border rounded z-20',
              row.field
                ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 border-gray-200 pr-6'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300',
            )}
          >
            {row.field ? (fieldConfig?.label ?? row.field) : 'Select field'}
            {row.field && (
              <div className="absolute -right-[9px] top-0 bottom-0 w-0 h-0 border-t-[17px] border-t-transparent border-b-[17px] border-b-transparent border-l-[10px] border-l-gray-200 group-hover:border-l-gray-300 transition-colors z-10" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="flex flex-col gap-1">
            {fields.map(f => (
              <button
                key={f.key}
                onClick={() => handleFieldSelect(f.key)}
                className={cn(
                  'px-3 py-2 text-sm rounded text-left hover:bg-gray-100 transition-colors whitespace-nowrap',
                  row.field === f.key && 'bg-gray-100 font-medium',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Operator selector — only after field is chosen */}
      {row.field && (
        <Popover open={operatorOpen} onOpenChange={setOperatorOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'group relative h-9 px-4 text-sm transition-colors border rounded z-10 -ml-3',
                row.operator
                  ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 border-gray-200 pr-6'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300',
              )}
            >
              <div className="pl-3">
                {row.operator
                  ? getOperatorLabel(fieldType, row.operator)
                  : 'Select operator'}
              </div>
              {row.operator && (
                <div className="absolute -right-[9px] top-0 bottom-0 w-0 h-0 border-t-[17px] border-t-transparent border-b-[17px] border-b-transparent border-l-[10px] border-l-gray-200 group-hover:border-l-gray-300 transition-colors z-10" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="flex flex-col gap-1">
              {availableOperators.map(op => (
                <button
                  key={op}
                  onClick={() => handleOperatorSelect(op)}
                  className={cn(
                    'px-3 py-2 text-sm rounded text-left hover:bg-gray-100 transition-colors whitespace-nowrap',
                    row.operator === op && 'bg-gray-100 font-medium',
                  )}
                >
                  {getOperatorLabel(fieldType, op)}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Value — only after operator is chosen; $empty takes no value */}
      {row.field && row.operator && row.operator !== '$empty' && (
        <Popover open={valueOpen} onOpenChange={setValueOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'relative h-9 px-4 text-sm transition-colors border rounded -ml-3',
                row.value
                  ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 border-gray-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300',
              )}
            >
              <div className="pl-3">{row.value || 'Enter value'}</div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="start">
            <Input
              autoFocus
              type={isNumeric ? 'number' : 'text'}
              value={row.value}
              onChange={e => onUpdate({ value: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter') setValueOpen(false)
              }}
              placeholder={
                isMultiValue ? 'Comma-separated values' : 'Enter value'
              }
            />
          </PopoverContent>
        </Popover>
      )}

      {/* Remove */}
      <button
        type="button"
        onClick={onRemove}
        className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors self-start mt-2"
        aria-label="Remove assertion"
      >
        <X className="h-4 w-4 text-gray-500" />
      </button>
    </div>
  )
}
